import { useEffect, useState } from "react";
import { Check, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import { http, errMsg } from "../../lib/api";

const cell = "w-full border border-white/10 bg-white/[0.03] px-2.5 py-2 text-sm outline-none focus:border-ember";

const PartRow = ({ p, onSaved, onDeleted }) => {
  const [d, setD] = useState({ name: p.name, asin: p.asin, socket: p.socket, est: p.est_price ?? "" });
  const dirty = d.name !== p.name || d.asin !== p.asin || d.socket !== p.socket || String(d.est) !== String(p.est_price ?? "");
  const patch = async (body) => {
    try {
      const { data } = await http.patch(`/admin/parts/${p.id}`, body);
      onSaved(data);
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const save = async () => {
    const est = d.est === "" ? null : Number(d.est);
    if (await patch({ name: d.name, asin: d.asin, socket: d.socket, ...(est === null ? { clear_price: true } : { est_price: est }) })) toast.success("Saved");
  };
  const del = async () => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await http.delete(`/admin/parts/${p.id}`).then(() => onDeleted(p.id)).catch((e) => toast.error(errMsg(e)));
  };
  return (
    <div data-testid={`part-row-${p.id}`} className="grid grid-cols-2 items-center gap-2 border-b border-white/[0.06] py-3 md:grid-cols-[1fr_110px_80px_100px_110px_auto]">
      <input className={`${cell} col-span-2 md:col-span-1`} value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} data-testid={`part-name-${p.id}`} />
      <input className={cell} placeholder="ASIN" value={d.asin} onChange={(e) => setD({ ...d, asin: e.target.value })} data-testid={`part-asin-${p.id}`} />
      <input className={cell} placeholder="Socket" value={d.socket} onChange={(e) => setD({ ...d, socket: e.target.value })} />
      <input className={cell} type="number" min="0" step="0.01" placeholder="Est. £" value={d.est} onChange={(e) => setD({ ...d, est: e.target.value })} data-testid={`part-price-${p.id}`} />
      <span className="text-xs">
        {p.source === "amazon" ? <span className="text-emerald-400">Amazon £{p.price}</span> : p.price != null ? <span className="text-slate-300">Est. £{p.price}</span> : <span className="text-amber-400">Needs price</span>}
      </span>
      <div className="col-span-2 flex items-center justify-end gap-2 md:col-span-1">
        <Switch checked={p.active} onCheckedChange={(v) => patch({ active: v })} data-testid={`part-active-${p.id}`} aria-label="Show on website" />
        <button disabled={!dirty} onClick={save} data-testid={`part-save-${p.id}`} className="grid h-8 w-8 place-items-center border border-white/10 hover:border-ember disabled:opacity-30"><Check className="h-4 w-4" /></button>
        <button onClick={del} data-testid={`part-delete-${p.id}`} className="grid h-8 w-8 place-items-center border border-white/10 text-slate-400 hover:border-red-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

const AddPart = ({ category, onAdded, prefill }) => {
  const [d, setD] = useState({ name: "", asin: "", est: "" });
  useEffect(() => {
    if (prefill) setD({ name: prefill.title || "", asin: prefill.asin || "", est: prefill.amount ?? "" });
  }, [prefill]);
  const add = async () => {
    if (d.name.trim().length < 2) return toast.error("Enter a part name");
    try {
      const { data } = await http.post("/admin/parts", { category, name: d.name.trim().slice(0, 150), asin: d.asin.trim(), est_price: d.est === "" ? null : Number(d.est) });
      onAdded(data);
      setD({ name: "", asin: "", est: "" });
      toast.success("Part added");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="grid grid-cols-2 gap-2 pt-3 md:grid-cols-[1fr_110px_100px_auto]">
      <input className={`${cell} col-span-2 md:col-span-1`} placeholder="New part name" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} data-testid={`add-part-name-${category}`} />
      <input className={cell} placeholder="ASIN" value={d.asin} onChange={(e) => setD({ ...d, asin: e.target.value })} />
      <input className={cell} type="number" min="0" step="0.01" placeholder="Est. £" value={d.est} onChange={(e) => setD({ ...d, est: e.target.value })} data-testid={`add-part-price-${category}`} />
      <button onClick={add} data-testid={`add-part-btn-${category}`} className="col-span-2 inline-flex items-center justify-center gap-1.5 bg-ember px-3 py-2 text-sm font-semibold text-ink hover:bg-amber-400 md:col-span-1"><Plus className="h-4 w-4" /> Add</button>
    </div>
  );
};

const AmazonSearch = ({ live, onPick }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [busy, setBusy] = useState(false);
  if (!live) return (
    <p className="border border-dashed border-white/15 p-4 text-sm text-slate-400" data-testid="amazon-not-connected">
      Live Amazon prices are off. Add your Amazon Creators API keys to switch on live prices and Amazon search. Until then, the website uses the estimated prices you enter below.
    </p>
  );
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setRes((await http.get("/admin/amazon/search", { params: { q } })).data || []); } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-white/10 p-4">
      <form onSubmit={run} className="flex gap-2">
        <input className={cell} placeholder="Search Amazon UK, e.g. RTX 5070" value={q} onChange={(e) => setQ(e.target.value)} data-testid="amazon-search-input" />
        <button className="inline-flex items-center gap-1.5 border border-white/10 px-3 text-sm hover:border-ember">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search</button>
      </form>
      <ul className="mt-3 space-y-2">{res.map((r) => (
        <li key={r.asin} className="flex items-center justify-between gap-3 text-sm">
          <span className="line-clamp-1 text-slate-300">{r.title}</span>
          <span className="shrink-0 font-mono">{r.amount != null ? `£${r.amount}` : "-"}</span>
          <button onClick={() => onPick(r)} className="shrink-0 border border-white/10 px-2 py-1 text-xs hover:border-ember">Use</button>
        </li>
      ))}</ul>
    </div>
  );
};

export const PartsManager = () => {
  const [data, setData] = useState(null);
  const [fee, setFee] = useState("");
  const [pct, setPct] = useState("");
  const [prefill, setPrefill] = useState(null);
  const [pickCat, setPickCat] = useState("cpu");
  useEffect(() => {
    http.get("/admin/parts").then((r) => { setData(r.data); setFee(String(r.data?.build_fee ?? 0)); setPct(String(r.data?.fee_percent ?? 0)); }).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!data) return <p className="mt-10 font-mono text-sm text-slate-400">Loading parts…</p>;
  const parts = Array.isArray(data.parts) ? data.parts : [];
  const upsert = (p) => setData((d) => ({ ...d, parts: d.parts.some((x) => x.id === p.id) ? d.parts.map((x) => (x.id === p.id ? p : x)) : [...d.parts, p] }));
  const remove = (id) => setData((d) => ({ ...d, parts: d.parts.filter((x) => x.id !== id) }));
  const saveFee = () => http.put("/admin/builder-settings", { build_fee: Number(fee) || 0, fee_percent: Number(pct) || 0 }).then(() => toast.success("Build fee saved")).catch((e) => toast.error(errMsg(e)));
  const needs = parts.filter((p) => p.price == null).length;

  return (
    <div className="mt-8 space-y-8" data-testid="parts-manager">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="border border-white/10 p-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Build fee added to every estimate</p>
          <div className="mt-2 grid grid-cols-[1fr_1fr_auto] gap-2">
            <label className="text-xs text-slate-400">% of parts
              <input className={`${cell} mt-1`} type="number" min="0" max="100" step="0.5" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="build-fee-percent-input" />
            </label>
            <label className="text-xs text-slate-400">Plus fixed £
              <input className={`${cell} mt-1`} type="number" min="0" step="1" value={fee} onChange={(e) => setFee(e.target.value)} data-testid="build-fee-input" />
            </label>
            <button onClick={saveFee} data-testid="build-fee-save-btn" className="self-end bg-ember px-4 py-2 text-sm font-semibold text-ink hover:bg-amber-400">Save</button>
          </div>
          <p className="mt-2 text-xs text-slate-400">Set both to 0 to show "Quoted separately".</p>
        </div>
        <AmazonSearch live={data.amazon_live} onPick={(r) => { setPrefill({ ...r }); toast.message(`Filled the "${pickCat}" add form`); }} />
      </div>
      {needs > 0 && <p className="text-sm text-amber-400" data-testid="parts-needs-price">{needs} part{needs > 1 ? "s" : ""} need a price before they show on the website.</p>}
      {data.categories.map((c) => (
        <section key={c.key} className="border border-white/[0.08] p-4 md:p-6" onFocus={() => setPickCat(c.key)}>
          <h3 className="font-display text-xl font-extrabold">{c.label}</h3>
          <div className="hidden grid-cols-[1fr_110px_80px_100px_110px_auto] gap-2 pt-3 font-mono text-[10px] uppercase tracking-wider text-slate-500 md:grid">
            <span>Name</span><span>Amazon ASIN</span><span>Socket</span><span>Est. price</span><span>On website</span><span />
          </div>
          {parts.filter((p) => p.category === c.key).map((p) => <PartRow key={p.id} p={p} onSaved={upsert} onDeleted={remove} />)}
          <AddPart category={c.key} onAdded={upsert} prefill={pickCat === c.key ? prefill : null} />
        </section>
      ))}
    </div>
  );
};
